import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from './api';
import Spinner from './Spinner';
import HamburgerMenu from './HamburgerMenu';

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    return {
        page: { backgroundColor: isDark ? '#121212' : '#f4f7f6', minHeight: '100vh', padding: '20px' },
        container: { maxWidth: '1200px', margin: '0 auto', fontFamily: 'sans-serif' },
        header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
        pageTitle: { fontSize: '28px', fontWeight: 'bold', color: isDark ? '#e0e0e0' : '#333' },
        controls: {
            display: 'flex',
            gap: '15px',
            marginBottom: '20px',
            flexWrap: 'wrap',
            alignItems: 'center'
        },
        filterButton: {
            padding: '8px 16px',
            borderRadius: '4px',
            border: `1px solid ${isDark ? '#444' : '#ddd'}`,
            backgroundColor: isDark ? '#1e1e1e' : '#fff',
            color: isDark ? '#e0e0e0' : '#333',
            cursor: 'pointer',
            fontSize: '14px'
        },
        activeFilter: {
            backgroundColor: isDark ? '#6cb2f7' : '#007bff',
            color: '#fff',
            border: `1px solid ${isDark ? '#6cb2f7' : '#007bff'}`
        },
        markAllReadButton: {
            padding: '8px 16px',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: isDark ? '#28a745' : '#28a745',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
        },
        notificationList: {
            backgroundColor: isDark ? '#1e1e1e' : '#fff',
            borderRadius: '8px',
            border: `1px solid ${isDark ? '#444' : '#ddd'}`,
            overflow: 'hidden'
        },
        notificationItem: {
            padding: '15px 20px',
            borderBottom: `1px solid ${isDark ? '#333' : '#eee'}`,
            cursor: 'pointer',
            transition: 'background-color 0.2s'
        },
        notificationItemUnread: {
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa'
        },
        notificationItemRead: {
            backgroundColor: 'transparent'
        },
        notificationHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '8px'
        },
        notificationTitle: {
            fontSize: '16px',
            fontWeight: 'bold',
            color: isDark ? '#e0e0e0' : '#333',
            flex: 1
        },
        notificationTime: {
            fontSize: '12px',
            color: isDark ? '#666' : '#999',
            marginLeft: '15px'
        },
        notificationMessage: {
            fontSize: '14px',
            color: isDark ? '#a0a0a0' : '#666',
            marginBottom: '8px',
            lineHeight: '1.5'
        },
        notificationMeta: {
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
            fontSize: '12px',
            color: isDark ? '#666' : '#999'
        },
        priorityBadge: {
            display: 'inline-block',
            padding: '3px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: 'bold'
        },
        priorityCritical: { backgroundColor: '#dc3545', color: 'white' },
        priorityHigh: { backgroundColor: '#fd7e14', color: 'white' },
        priorityMedium: { backgroundColor: '#ffc107', color: '#333' },
        priorityLow: { backgroundColor: '#6c757d', color: 'white' },
        emptyState: {
            padding: '60px 20px',
            textAlign: 'center',
            color: isDark ? '#a0a0a0' : '#666'
        },
        link: { color: isDark ? '#6cb2f7' : '#007bff', textDecoration: 'none' }
    };
};

function NotificationsPage({ theme }) {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, unread, read
    const styles = getStyles(theme);

    const fetchNotifications = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get('/notifications/?ordering=-created_at');
            const data = Array.isArray(response.data) 
                ? response.data 
                : (response.data.results || []);
            setNotifications(data);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchNotifications();
        // Refresh every 30 seconds
        const interval = setInterval(fetchNotifications, 30000);
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    const markAsRead = async (notificationId) => {
        try {
            await api.post(`/notifications/${notificationId}/mark_read/`);
            setNotifications(prev => prev.map(n => 
                n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
            ));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            await api.post('/notifications/mark_all_read/');
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    const handleNotificationClick = async (notification) => {
        // Fix: Await markAsRead before navigating to ensure the API call completes
        if (!notification.is_read) {
            await markAsRead(notification.id);
        }
        
        if (notification.action_url) {
            try {
                // Parse the URL to determine if it's external or internal
                const url = new URL(notification.action_url, window.location.origin);
                
                // Check if it's an external URL (different origin)
                const isExternal = url.origin !== window.location.origin;
                
                if (isExternal) {
                    // Bug Fix 2: External URL - Use window.location.href to navigate to different domain
                    window.location.href = notification.action_url;
                } else {
                    // Bug Fix 1: Internal URL - Use React Router navigate, preserving pathname, search, AND hash
                    const internalPath = url.pathname + url.search + url.hash;
                    navigate(internalPath);
                }
            } catch (error) {
                // If URL parsing fails, treat as relative path and use React Router
                // This handles cases where action_url is just a path like '/status-board?buno=123#section'
                if (notification.action_url.startsWith('/')) {
                    navigate(notification.action_url);
                } else {
                    // Fallback to window.location.href for malformed URLs
                    console.warn('Invalid action_url format:', notification.action_url);
                    window.location.href = notification.action_url;
                }
            }
        }
    };

    const getPriorityStyle = (priority) => {
        switch (priority) {
            case 'critical': return { ...styles.priorityBadge, ...styles.priorityCritical };
            case 'high': return { ...styles.priorityBadge, ...styles.priorityHigh };
            case 'medium': return { ...styles.priorityBadge, ...styles.priorityMedium };
            case 'low': return { ...styles.priorityBadge, ...styles.priorityLow };
            default: return { ...styles.priorityBadge, ...styles.priorityMedium };
        }
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    const filteredNotifications = filter === 'all' 
        ? notifications 
        : filter === 'unread'
        ? notifications.filter(n => !n.is_read)
        : notifications.filter(n => n.is_read);

    const unreadCount = notifications.filter(n => !n.is_read).length;

    return (
        <div style={styles.page}>
            <div style={styles.container}>
                <div style={styles.header}>
                    <div>
                        <Link to="/" style={styles.link}>&larr; Back to Home</Link>
                        <h1 style={styles.pageTitle}>Notifications</h1>
                        {unreadCount > 0 && (
                            <p style={{ color: styles.link.color, marginTop: '5px' }}>
                                {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
                            </p>
                        )}
                    </div>
                    <HamburgerMenu theme={theme} />
                </div>

                <div style={styles.controls}>
                    <button
                        style={{ ...styles.filterButton, ...(filter === 'all' ? styles.activeFilter : {}) }}
                        onClick={() => setFilter('all')}
                    >
                        All ({notifications.length})
                    </button>
                    <button
                        style={{ ...styles.filterButton, ...(filter === 'unread' ? styles.activeFilter : {}) }}
                        onClick={() => setFilter('unread')}
                    >
                        Unread ({unreadCount})
                    </button>
                    <button
                        style={{ ...styles.filterButton, ...(filter === 'read' ? styles.activeFilter : {}) }}
                        onClick={() => setFilter('read')}
                    >
                        Read ({notifications.length - unreadCount})
                    </button>
                    {unreadCount > 0 && (
                        <button
                            style={styles.markAllReadButton}
                            onClick={markAllAsRead}
                        >
                            Mark All as Read
                        </button>
                    )}
                </div>

                {loading ? (
                    <div style={styles.emptyState}>
                        <Spinner />
                    </div>
                ) : filteredNotifications.length === 0 ? (
                    <div style={styles.notificationList}>
                        <div style={styles.emptyState}>
                            {filter === 'unread' 
                                ? 'No unread notifications' 
                                : filter === 'read'
                                ? 'No read notifications'
                                : 'No notifications'}
                        </div>
                    </div>
                ) : (
                    <div style={styles.notificationList}>
                        {filteredNotifications.map(notification => (
                            <div
                                key={notification.id}
                                style={{
                                    ...styles.notificationItem,
                                    ...(notification.is_read 
                                        ? styles.notificationItemRead 
                                        : styles.notificationItemUnread)
                                }}
                                onClick={() => handleNotificationClick(notification)}
                            >
                                <div style={styles.notificationHeader}>
                                    <div style={styles.notificationTitle}>
                                        {notification.title}
                                        <span style={getPriorityStyle(notification.priority)}>
                                            {notification.priority}
                                        </span>
                                    </div>
                                    <div style={styles.notificationTime}>
                                        {formatTime(notification.created_at)}
                                    </div>
                                </div>
                                <div style={styles.notificationMessage}>
                                    {notification.message}
                                </div>
                                <div style={styles.notificationMeta}>
                                    {notification.related_aircraft_buno && (
                                        <span>Aircraft: {notification.related_aircraft_buno}</span>
                                    )}
                                    {notification.notification_type && (
                                        <span>• Type: {notification.notification_type.replace('_', ' ')}</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default NotificationsPage;

